<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="deep-gl" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("MAPPINGS.LIST") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('mappings-create')"
          class="mr-4"
          :disabled="isProcessing"
          @click="downloadTemplate"
        >
          {{ $t("MAPPINGS.DOWNLOAD_TEMPLATE") }}
        </v-btn>
        <v-btn
          v-if="hasPermissions('mappings-create')"
          class="mr-4"
          :disabled="isProcessing"
          @click="importMapping"
        >
          {{ $t("MAPPINGS.IMPORT_MAPPING") }}
        </v-btn>
        <v-btn
          v-if="hasPermissions('mappings-create')"
          class="mr-4 warning"
          :disabled="isProcessing"
          :loading="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
        <input
          ref="uploader"
          class="d-none"
          type="file"
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          @change="upload"
        >
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="mappings-table"
              height="500"
              name="mappings-table"
              :headers="headers"
              :hidden-headers="hiddenHeaders"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in columnHeaders"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <TableRowActions
                  v-if="header.value === 'options'"
                  :key="index"
                  :show-delete="false"
                >
                  <template #view>
                    <v-btn
                      v-if="hasPermissions([
                        'mapping-values-view-self',
                        'mapping-values-view-team',
                        'mapping-values-view-all',
                      ])"
                      icon
                      color="indigo"
                      :to="{ name: 'edit-mapping-values', params: { mappingId: item.item.id } }"
                      @click="rememberTableRow(item.item.id)"
                    >
                      <v-icon dark>
                        mdi-playlist-edit
                      </v-icon>
                    </v-btn>
                  </template>
                  <template #edit>
                    <v-btn
                      v-if="hasPermissions('mappings-edit')"
                      icon
                      color="primary"
                      :to="{ name: 'edit-mappings', params: { id: item.item.id } }"
                      @click="rememberTableRow(item.item.id)"
                    >
                      <v-icon dark>
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                  </template>
                </TableRowActions>
                <template v-else>
                  {{ item.value ?? "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TableRowActions from "@/views/partials/v-table/components/TableRowActions.vue";
import {
  GET_MAPPINGS_LIST,
  UPLOAD_MAPPING,
  DOWNLOAD_MAPPING,
} from "@/store/mappings.module";
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";

export default {
  name: "ShowMappings",
  components: {
    Table,
    TableRowActions,
  },
  data() {
    return {
      tableName: "mappings-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_MAPPINGS_LIST,
      file: null,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "35%",
        },
        {
          text: this.$t("MAPPINGS.TAG"),
          value: "tag",
          align: "center",
          width: "25%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "12%",
        },
      ],
      hiddenHeaders: [],
    };
  },
  computed: {
    columnHeaders() {
      return [...this.headers, ...this.hiddenHeaders];
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: this.$t("MAPPINGS.LIST") },
    ]);

    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    const selectedHiddenHeadersKey = `table-state.${this.tableName}.selectedHiddenHeaders`;
    const selectedHiddenHeadersInitializedKey = `table-state.${this.tableName}.selectedHiddenHeadersInitialized`;

    if (!userStateService.getState(selectedHiddenHeadersInitializedKey, false)) {
      userStateService.setState(selectedHiddenHeadersKey, [...this.hiddenHeaders]);
      userStateService.setState(selectedHiddenHeadersInitializedKey, true);
    }

    this.userStatePrepared = true;
  },
  methods: {
    handleCreateClick() {
      this.$router.push({ name: "create-mappings" });
    },
    rememberTableRow(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );
    },
    importMapping() {
      this.$refs.uploader.click();
    },
    downloadTemplate() {
      this.isProcessing = true;

      this.$store
        .dispatch(DOWNLOAD_MAPPING)
        .then((resource) => {
          location.href = resource.url;
        })
        .catch(() => {})
        .finally(() => {
          this.isProcessing = false;
        });
    },
    upload(e) {
      this.file = e ? e.target.files[0] : this.file;

      if (!this.file) {
        return;
      }

      const formData = new FormData();
      formData.append("file", this.file);
      this.isProcessing = true;

      this.$store
        .dispatch(UPLOAD_MAPPING, { params: formData })
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: "Mapping upload successfuly.",
          });
          this.$refs["mappings-table"]?.fetchServerData();
        })
        .catch(() => {})
        .finally(() => {
          this.isProcessing = false;
          if (this.$refs.uploader) {
            this.$refs.uploader.value = "";
          }
        });
    },
  },
};
</script>
